import { useState } from 'react';
import { Alert, App, Button, Card, Form, Input, Typography } from 'antd';
import { Fire } from '@phosphor-icons/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { staffLogin } from '../auth/api';
import { ADMIN_WEB_ROLES, useSession } from '../auth/session';
import { normalizeStaffPassword } from '../auth/staffPassword';
import { ApiError, NetworkError } from '../api/client';
import { tokens } from '../theme';

interface LoginFormValues {
  username: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useSession((state) => state.signIn);
  const { message } = App.useApp();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const rawFrom = (location.state as { from?: string } | null)?.from;
  // /doi-mat-khau chỉ hợp lệ khi server còn cờ mustChangePassword; không dùng làm
  // đích quay lại sau login thường (tránh vòng lặp đổi MK → login → đổi MK).
  const redirectTo =
    rawFrom && rawFrom !== '/doi-mat-khau' ? rawFrom : '/';

  const handleSubmit = async (values: LoginFormValues): Promise<void> => {
    setIsSubmitting(true);
    setErrorText(null);

    try {
      // KHÔNG gửi branchId và shiftLabel: đây là đăng nhập Admin Web, không mở
      // ca làm tại quầy. Backend chỉ cho super admin bỏ trống hai trường này.
      const result = await staffLogin({
        username: values.username,
        password: normalizeStaffPassword(values.password),
        client: 'admin',
      });

      if (!ADMIN_WEB_ROLES.includes(result.role)) {
        // Server đã cấp token hợp lệ, nhưng vai trò này thuộc Staff App.
        // Nói rõ ngay thay vì để họ vào rồi gặp 403 ở mọi thao tác.
        setErrorText(
          'Tài khoản thu ngân và lễ tân làm việc trên Staff App, không dùng được Admin Web.',
        );
        return;
      }

      signIn(
        {
          staffId: result.staffId,
          name: result.name,
          role: result.role,
          branchId: result.branchId,
          accessToken: result.accessToken,
          mustChangePassword: result.mustChangePassword,
        },
        result.refreshToken,
      );

      if (result.mustChangePassword) {
        message.warning('Cần đổi mật khẩu trước khi dùng các chức năng khác.');
        navigate('/doi-mat-khau', { replace: true });
        return;
      }

      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (error instanceof ApiError || error instanceof NetworkError) {
        setErrorText(error.message);
      } else {
        setErrorText('Có lỗi không xác định. Thử lại hoặc báo kỹ thuật.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        background: tokens.color.canvas,
        padding: tokens.space.sectionGap,
      }}
    >
      <Card style={{ width: 380, boxShadow: tokens.shadow.card }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: tokens.radius.control,
              background: tokens.color.green,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
            }}
          >
            <Fire size={20} weight="fill" aria-hidden />
          </span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Reski HQ</div>
            <div style={{ fontSize: 11, color: tokens.color.ink3 }}>Motives Vietnam</div>
          </div>
        </div>

        {errorText !== null && (
          <Alert
            style={{ marginBottom: 16 }}
            type="error"
            showIcon
            message={errorText}
            closable
            onClose={() => setErrorText(null)}
          />
        )}

        {/* Nhãn nằm TRÊN ô nhập, không dùng placeholder thay nhãn (quy tắc 3, 09-design-system) */}
        <Form<LoginFormValues> layout="vertical" requiredMark={false} onFinish={handleSubmit}>
          <Form.Item
            label="Tên đăng nhập"
            name="username"
            rules={[{ required: true, message: 'Chưa nhập tên đăng nhập.' }]}
          >
            <Input size="large" autoComplete="username" autoFocus />
          </Form.Item>

          <Form.Item
            label="Mật khẩu"
            name="password"
            rules={[
              {
                validator: async (_, value: string | undefined) => {
                  if (normalizeStaffPassword(value ?? '').length === 0) {
                    return Promise.reject(new Error('Chưa nhập mật khẩu.'));
                  }
                },
              },
            ]}
          >
            <Input.Password size="large" autoComplete="current-password" />
          </Form.Item>

          <Button type="primary" size="large" block htmlType="submit" loading={isSubmitting}>
            Đăng nhập
          </Button>
        </Form>

        <Typography.Paragraph
          style={{ marginTop: 16, marginBottom: 0, fontSize: 12, color: tokens.color.ink3 }}
        >
          Tài khoản do quản trị hệ thống cấp. Quên mật khẩu thì liên hệ quản trị để đặt lại.
        </Typography.Paragraph>
        <Link
          to="/ho-tro"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 44,
            marginTop: 4,
            fontSize: 13,
            fontWeight: 600,
            color: tokens.color.orange600,
          }}
        >
          Trung tâm hỗ trợ
        </Link>
      </Card>
    </div>
  );
}
